import { useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail, absoluteUrl } from "@/lib/api";
import { Plus, X, UploadSimple, Trash, Eye, EyeSlash, ArrowUp, ArrowDown, LinkSimple, ImageSquare, Share, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";

const SERVICE_TYPES = ["flyer", "logo", "seal", "banner", "web", "other"];
const PLATFORMS = ["Shopify", "WordPress", "Squarespace", "Wix", "Webflow", "Custom Coded"];

export default function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [capturing, setCapturing] = useState(null);
  const fileRef = useRef(null);

  function blankForm() {
    return {
      title: "",
      description: "",
      service_type: "web",
      external_url: "",
      client_name: "",
      platform: "",
      is_published: false,
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/portfolio");
      setItems(data);
    } catch (e) {
      toast.error("Could not load portfolio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setCreateOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      description: item.description || "",
      service_type: item.service_type || "logo",
      external_url: item.external_url || "",
      client_name: item.client_name || "",
      platform: item.platform || "",
      is_published: !!item.is_published,
    });
    setCreateOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title required");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/admin/portfolio/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/admin/portfolio", form);
        toast.success(form.external_url ? "Created — use \"Capture from site\" for an instant cover" : "Created — now upload a cover image");
      }
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const togglePublish = async (item) => {
    try {
      await api.patch(`/admin/portfolio/${item.id}`, { is_published: !item.is_published });
      await load();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const copyShareLink = async (item) => {
    // Use the production domain for the unfurl-friendly link, falling back to the SPA origin in preview
    const origin = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
    const url = `${origin.replace(/\/$/, "")}/api/share/work/${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — paste into Slack, LinkedIn, or iMessage");
    } catch (_) {
      // Fallback: show the URL in a prompt for manual copy
      window.prompt("Copy this share link:", url);
    }
  };

  const reorder = async (item, direction) => {
    const idx = items.findIndex((x) => x.id === item.id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const a = items[idx];
    const b = items[target];
    try {
      await Promise.all([
        api.patch(`/admin/portfolio/${a.id}`, { display_order: b.display_order }),
        api.patch(`/admin/portfolio/${b.id}`, { display_order: a.display_order }),
      ]);
      await load();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/portfolio/${item.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const onUploadCover = async (item, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/portfolio/${item.id}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover uploaded");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const captureCover = async (item) => {
    setCapturing(item.id);
    try {
      await api.post(`/admin/portfolio/${item.id}/capture-cover`);
      toast.success("Live screenshot captured");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Capture failed");
    } finally {
      setCapturing(null);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Portfolio</div>
          <h1 className="font-display text-5xl mt-2">Showcase work</h1>
          <p className="text-sm text-[#a3a3a3] mt-2 max-w-xl">
            Add finished pieces (manual or from delivered projects). Toggle visibility to publish on the public landing page.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="portfolio-new"
          className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
        >
          <Plus size={16} weight="bold" /> New item
        </button>
      </div>

      {loading ? (
        <div className="text-[#a3a3a3]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-12 text-center">
          <ImageSquare size={36} className="mx-auto mb-3 text-[#a3a3a3]" />
          <div className="text-[#a3a3a3] mb-4">No portfolio items yet.</div>
          <button onClick={openCreate} className="btn-outline px-4 py-2 text-sm uppercase tracking-wider">
            Add your first piece
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="portfolio-grid">
          {items.map((item, idx) => (
            <div key={item.id} data-testid={`portfolio-${item.id}`} className="border border-[#262626] bg-[#0a0a0a] flex flex-col">
              <div className="aspect-[4/3] bg-[#0f0f0f] relative overflow-hidden border-b border-[#262626]">
                {item.cover_url ? (
                  <img src={`${absoluteUrl(item.cover_url)}?w=400`} alt={item.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-[#444] gap-2">
                    <ImageSquare size={32} />
                    <span className="text-xs">No cover image</span>
                  </div>
                )}
                <span className={`absolute top-2 right-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${
                  item.is_published ? "border-[#FF2D2D] bg-[#FF2D2D] text-white" : "border-[#404040] bg-black/60 text-[#a3a3a3]"
                }`}>
                  {item.is_published ? "Live" : "Draft"}
                </span>
                <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#262626] bg-black/70 text-[#a3a3a3]">
                  {item.source}
                </span>
              </div>

              <div className="p-4 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">
                  {item.service_type}{item.platform && <span className="text-[#a3a3a3]"> · {item.platform}</span>}
                </div>
                <h3 className="font-display text-xl mt-1 truncate" title={item.title}>{item.title}</h3>
                {item.client_name && <div className="text-xs text-[#a3a3a3] mt-1">For {item.client_name}</div>}
                {item.description && (
                  <p className="text-sm text-[#cfcfcf] mt-2 line-clamp-2">{item.description}</p>
                )}
                {item.external_url && (
                  <a
                    href={item.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#FF2D2D] hover:underline mt-2 flex items-center gap-1"
                  >
                    <LinkSimple size={12} /> {item.external_url}
                  </a>
                )}

                <div className="mt-auto pt-4 flex flex-wrap gap-2">
                  <label className="btn-outline px-2.5 py-1.5 text-xs flex items-center gap-1.5 cursor-pointer">
                    <UploadSimple size={12} /> {item.cover_url ? "Replace" : "Upload"} cover
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onUploadCover(item, e.target.files?.[0])}
                      data-testid={`upload-cover-${item.id}`}
                    />
                  </label>
                  {item.external_url && (
                    <button
                      onClick={() => captureCover(item)}
                      disabled={capturing === item.id}
                      data-testid={`capture-cover-${item.id}`}
                      className="btn-outline px-2.5 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50"
                      title="Take a live screenshot of the website and use it as the cover"
                    >
                      <Camera size={12} /> {capturing === item.id ? "Capturing…" : "Capture from site"}
                    </button>
                  )}
                  <button
                    onClick={() => togglePublish(item)}
                    data-testid={`toggle-publish-${item.id}`}
                    className="btn-outline px-2.5 py-1.5 text-xs flex items-center gap-1.5"
                  >
                    {item.is_published ? <><EyeSlash size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                  </button>
                  <button onClick={() => openEdit(item)} data-testid={`edit-${item.id}`} className="btn-outline px-2.5 py-1.5 text-xs">
                    Edit
                  </button>
                  {item.is_published && (
                    <button
                      onClick={() => copyShareLink(item)}
                      data-testid={`copy-share-${item.id}`}
                      className="btn-outline px-2.5 py-1.5 text-xs flex items-center gap-1.5"
                      title="Copy a Slack/LinkedIn-friendly share link with branded preview card"
                    >
                      <Share size={12} /> Share link
                    </button>
                  )}
                  <div className="ml-auto flex gap-1">
                    <button
                      onClick={() => reorder(item, "up")}
                      disabled={idx === 0}
                      className="px-2 py-1.5 text-xs border border-[#262626] text-[#a3a3a3] hover:text-white disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      onClick={() => reorder(item, "down")}
                      disabled={idx === items.length - 1}
                      className="px-2 py-1.5 text-xs border border-[#262626] text-[#a3a3a3] hover:text-white disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown size={12} />
                    </button>
                    <button
                      onClick={() => remove(item)}
                      className="px-2 py-1.5 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                      title="Delete"
                      data-testid={`delete-${item.id}`}
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="portfolio-modal">
          <div className="border border-[#262626] bg-[#0a0a0a] w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">{editing ? "Edit" : "New"}</div>
                <h3 className="font-display text-2xl mt-1">{editing ? "Edit item" : "Add portfolio item"}</h3>
              </div>
              <button onClick={() => setCreateOpen(false)} className="text-[#a3a3a3] hover:text-white"><X size={18} /></button>
            </div>

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="form-title"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Description</label>
            <textarea
              rows={4}
              value={form.description}
              data-testid="form-description"
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none resize-none"
            />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Type</label>
                <select
                  value={form.service_type}
                  data-testid="form-service-type"
                  onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-3 focus:border-[#FF2D2D] outline-none"
                >
                  {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Client (optional)</label>
                <input
                  type="text"
                  value={form.client_name}
                  data-testid="form-client"
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-3 focus:border-[#FF2D2D] outline-none"
                />
              </div>
            </div>

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">External URL (optional, e.g. live site)</label>
            <input
              type="url"
              placeholder="https://"
              value={form.external_url}
              data-testid="form-url"
              onChange={(e) => setForm({ ...form, external_url: e.target.value })}
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Platform tag (optional, e.g. Shopify)</label>
            <input
              type="text"
              list="portfolio-platforms"
              placeholder="Shopify, WordPress, Custom Coded…"
              value={form.platform}
              data-testid="form-platform"
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />
            <datalist id="portfolio-platforms">
              {PLATFORMS.map((p) => <option key={p} value={p} />)}
            </datalist>

            <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                data-testid="form-publish"
              />
              <span>Publish immediately</span>
            </label>

            <div className="flex gap-3 mt-6">
              <button onClick={save} data-testid="form-save" className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider">
                {editing ? "Save changes" : "Create"}
              </button>
              <button onClick={() => setCreateOpen(false)} className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">
                Cancel
              </button>
            </div>
            {!editing && (
              <p className="text-xs text-[#a3a3a3] mt-3 text-center">After creating, upload a cover or click "Capture from site" on the card.</p>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
